/**
 * Every photograph on the site, framed the way the studio chose (D-199).
 *
 * <b>The point of this existing is that there was nowhere to fix it.</b> Eleven
 * call sites wrote their own `object-cover`, and every one crops from the
 * centre — so a photograph with a dancer high in frame loses her head in a wide
 * hero and looks fine as a thumbnail. One component means one place, and the
 * framing is simply the data it reads.
 *
 * <b>Centred at zoom 1 is a real default, not a placeholder.</b> It is byte for
 * byte what `object-fit: cover` did before any of this existed, so an
 * unframed photograph renders exactly as it always has.
 */
export type Framing = {
  /** 0 (left) to 100 (right). */
  focalX: number
  /** 0 (top) to 100 (bottom). */
  focalY: number
  /** 1 fills the box; above that crops in further. */
  zoom: number
}

export const CENTRED: Framing = { focalX: 50, focalY: 50, zoom: 1 }

export default function StudioImage({
  src,
  alt,
  framing,
  className,
  loading = 'lazy',
}: {
  src: string
  /**
   * Empty string is a deliberate choice, not an oversight — a decorative
   * photograph beside its own heading is announced twice otherwise.
   */
  alt: string
  framing?: Framing | null
  /** Shape and radius. The component owns the fit, the caller owns the box. */
  className?: string
  loading?: 'lazy' | 'eager'
}) {
  const { focalX, focalY, zoom } = framing ?? CENTRED

  /*
   * Zoom is a scale on the image rather than a change of size, so the box keeps
   * its layout and only the crop tightens. `transform-origin` sits on the focal
   * point, which is what makes zooming feel like it pulls toward what was
   * chosen rather than toward the middle.
   */
  const zoomed = zoom > 1

  return (
    <span className={`block overflow-hidden ${className ?? ''}`}>
      <img
        src={src}
        alt={alt}
        loading={loading}
        className="size-full object-cover"
        style={{
          objectPosition: `${focalX}% ${focalY}%`,
          ...(zoomed
            ? { transform: `scale(${zoom})`, transformOrigin: `${focalX}% ${focalY}%` }
            : null),
        }}
      />
    </span>
  )
}

/**
 * The framing to use, resolved in the order the spec sets out.
 *
 * Per-placement override, then the photograph's own default, then centred. A
 * good default is usually right everywhere — `cover` keeps the focal point in
 * frame at any shape — so the override is the escape hatch for the case that
 * genuinely differs: a group shot as a wide banner and as a 64px icon.
 */
export function resolveFraming(
  override: Framing | null | undefined,
  assetDefault: Framing | null | undefined,
): Framing {
  return override ?? assetDefault ?? CENTRED
}

/** Whether this framing does anything an unframed image would not. */
export function isCentred(framing: Framing | null | undefined): boolean {
  const f = framing ?? CENTRED
  return f.focalX === 50 && f.focalY === 50 && f.zoom === 1
}

/**
 * The same framing, for a CSS background rather than an <c>&lt;img&gt;</c>.
 *
 * <b>The homepage hero is a background image and cannot be anything else</b> —
 * the headline sits over it and it is dimmed behind the words, which is the
 * whole point of the element. So it cannot use `object-position`, and without
 * this it was the one placement on the site that ignored framing (D-253).
 *
 * `background-position` takes the focal point directly. Zoom is the same scale
 * `StudioImage` applies, anchored the same way — <b>the caller must clip it</b>,
 * because a scaled background layer paints outside its own box.
 */
export function backgroundFraming(framing: Framing | null | undefined): {
  backgroundPosition: string
  transform?: string
  transformOrigin?: string
} {
  const { focalX, focalY, zoom } = framing ?? CENTRED
  const point = `${focalX}% ${focalY}%`

  return {
    backgroundPosition: point,
    ...(zoom > 1 ? { transform: `scale(${zoom})`, transformOrigin: point } : null),
  }
}
